import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AddressAutocomplete } from "@/components/common/AddressAutocomplete";
import { MapCanvas } from "@/components/common/MapCanvas";
import { SkeletonMap } from "@/components/common/Skeletons";
import { orgService } from "@/services/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, MapPin } from "lucide-react";

const EMPTY = { name: "", address: "" };

// Two steps, not one form with lat/long boxes:
//   1. Name + address, the address being a real Google Places search.
//   2. A map opened on that address, where the pin is dragged onto the actual
//      gate and Save writes the facility.
// Nobody types coordinates any more — those fields were the only way to put a
// facility on the map before, and asking a fleet manager for six decimal
// places of latitude was never the right question.

// The dialog animates in over ~200ms (duration-200 + zoom-in-95 on
// DialogContent). A Google Map initialised while its container is still
// mid-transform renders as a black rectangle, so the map is mounted only once
// the dialog has settled. This is why the map step shows a skeleton first.
const MAP_MOUNT_DELAY_MS = 260;

export function AddFacilityDialog({ open, onOpenChange, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [step, setStep] = useState("details"); // "details" | "map"
  // Where the address resolved to. Kept separate from `pin` so the map can be
  // framed on the address while the pin is free to move.
  const [anchor, setAnchor] = useState(null);
  const [pin, setPin] = useState(null);
  const [mapMounted, setMapMounted] = useState(false);

  // Reset on close so the next Add starts clean rather than resuming someone
  // else's half-finished facility.
  useEffect(() => {
    if (open) return;
    setForm(EMPTY);
    setStep("details");
    setAnchor(null);
    setPin(null);
    setSaving(false);
  }, [open]);

  useEffect(() => {
    if (step !== "map") {
      setMapMounted(false);
      return;
    }
    const timer = setTimeout(() => setMapMounted(true), MAP_MOUNT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [step]);

  const goToMap = () => {
    if (!form.name.trim() || !anchor) return;
    setPin({ lat: anchor.lat, lng: anchor.lng });
    setStep("map");
  };

  const onAddressResolved = (hit) => {
    setAnchor({ lat: hit.lat, lng: hit.lng });
    // Straight to the map, which is the point of resolving an address at all.
    // Only if the facility already has a name, so the flow can't skip past a
    // required field.
    if (form.name.trim()) {
      setPin({ lat: hit.lat, lng: hit.lng });
      setStep("map");
    }
  };

  const save = async () => {
    if (!pin) return;
    setSaving(true);
    try {
      await orgService.createFacility({
        name: form.name,
        address: form.address,
        latitude: pin.lat,
        longitude: pin.lng,
      });
      toast.success("Facility added");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not save facility");
    } finally {
      setSaving(false);
    }
  };

  const isMapStep = step === "map";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={isMapStep ? "sm:max-w-3xl" : "sm:max-w-md"}
        data-testid="add-facility-dialog"
      >
        <DialogHeader>
          <DialogTitle>{isMapStep ? "Pin the exact location" : "Add Facility"}</DialogTitle>
          <DialogDescription>
            {isMapStep
              ? "Click the map to move the pin, or drag it. Put it on the entrance you actually use, then save."
              : "Register a new yard, depot or operational site."}
          </DialogDescription>
        </DialogHeader>

        {isMapStep ? (
          <>
            <div className="space-y-3">
              <div className="flex items-center gap-2 rounded-xl border border-border/60 bg-secondary/20 px-3 py-2 text-sm">
                <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate">
                  <span className="font-medium">{form.name}</span>
                  {form.address && <span className="text-muted-foreground"> · {form.address}</span>}
                </span>
                <span className="shrink-0 font-telemetry text-xs text-muted-foreground">
                  {pin ? `${pin.lat.toFixed(5)}, ${pin.lng.toFixed(5)}` : "—"}
                </span>
              </div>
              <div className="h-[420px]">
                {mapMounted ? (
                  <MapCanvas
                    className="h-full"
                    drawingActive
                    drawingMode="pin"
                    initialPin={anchor}
                    onShapeDrawn={({ lat, lng }) => setPin({ lat, lng })}
                    testId="facility-pin-map"
                  />
                ) : (
                  <SkeletonMap />
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setStep("details")}>
                <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
              </Button>
              <Button onClick={save} disabled={saving || !pin} data-testid="add-facility-submit">
                <Check className="mr-1.5 h-4 w-4" /> {saving ? "Saving…" : "Save Facility"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); goToMap(); }} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input
                placeholder="North Yard"
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="facility-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Address</Label>
              <AddressAutocomplete
                value={form.address}
                onChange={(address) => {
                  setForm((f) => ({ ...f, address }));
                  // Editing the address invalidates the point it resolved to.
                  setAnchor(null);
                }}
                onResolved={onAddressResolved}
                placeholder="Start typing an address…"
                testId="facility-address-input"
              />
              <p className="text-xs text-muted-foreground">
                Pick a suggestion (or press Enter) and the map opens so you can drop the pin.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={!form.name.trim() || !anchor} data-testid="facility-continue-to-map">
                Continue <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
